import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Landmark, Radar, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const LAYERS = [
  {
    n: "Layer 01",
    title: "The Protected Monument",
    body: "Untouched physical assets. No drilling. No construction. No permanent installations. The site remains sovereign.",
    Icon: Landmark,
  },
  {
    n: "Layer 02",
    title: "The Invisible Data Mesh",
    body: "Geo-spatial mapping and centimetre-precise anchors — entirely digital, invisible to the eye, undisruptive to the site.",
    Icon: Radar,
  },
  {
    n: "Layer 03",
    title: "The Experiential Overlay",
    body: "The reconstruction — visual, ambient, cinematic — delivered exclusively through visitor hardware. Reversible, upgradable, endless.",
    Icon: Sparkles,
  },
];

export default function ZeroIntervention() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bigY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <section
      ref={ref}
      data-testid="intervention-section"
      className="relative py-14 md:py-20 border-t border-gold/10 overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ y: bigY }}
        className="absolute inset-0 pointer-events-none flex items-center justify-center"
      >
        <div className="w-[70vw] h-[70vw] max-w-[720px] max-h-[720px] rounded-full border border-gold/10" />
      </motion.div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 font-mono-accent text-[11px] text-gold/80">
            <span>Chapter 04</span>
            <span className="w-8 h-px bg-gold/40" />
            <span className="text-bone/50">The Guarantee</span>
          </div>
          <h2 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] text-bone tracking-tight whitespace-nowrap">
            Zero physical {" "}
            <em className="text-gold not-italic">intervention.</em>
          </h2>
          <p className="mt-6 text-bone/70 text-lg leading-relaxed max-w-xl">
            Developed in strict consultation with the Archaeological Survey of
            India and heritage conservators. Three layers — one guarantee: the
            monument itself is never touched.
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid md:grid-cols-3 gap-6">
          {LAYERS.map((l, i) => {
            const Icon = l.Icon;
            return (
              <motion.div
                key={l.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
                className={`lift relative border border-gold/20 bg-surface p-8 md:p-10 flex flex-col ${
                  i === 1 ? "md:translate-y-6" : i === 2 ? "md:translate-y-12" : ""
                }`}
                data-testid={`layer-card-${i + 1}`}
              >
                <div className="font-display text-6xl md:text-7xl text-gold/25 leading-none">
                  0{i + 1}
                </div>
                <div className="mt-5 flex items-center gap-3 font-mono-accent text-[10px] text-gold">
                  {l.n}
                  <span className="w-10 h-px bg-gold/40" />
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl text-bone leading-tight">
                  {l.title}
                </h3>
                <p className="mt-4 text-bone/65 text-base leading-relaxed">
                  {l.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
